import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const CapacityPlanningSection = ({ capacityData, suppliers, countries }) => {
  const [selectedCountry, setSelectedCountry] = useState('all');

  const filteredData = selectedCountry === 'all' 
    ? capacityData 
    : capacityData?.filter(c => c?.countryId === selectedCountry);

  const totalSuppliers = capacityData?.reduce((sum, c) => sum + (c?.supplierCount || 0), 0);
  const totalMachines = capacityData?.reduce((sum, c) => sum + (c?.machineCount || 0), 0);
  const averageCapacity = capacityData?.length > 0 
    ? (totalMachines / capacityData?.length)?.toFixed(1) 
    : 0;

  const getCapacityUtilization = (machineCount) => {
    if (machineCount === 0) return { level: 'Aucune capacité', color: 'text-gray-600', bgColor: 'bg-gray-100', percentage: 0 };
    if (machineCount <= 5) return { level: 'Capacité faible', color: 'text-yellow-600', bgColor: 'bg-yellow-100', percentage: 30 };
    if (machineCount <= 15) return { level: 'Capacité moyenne', color: 'text-blue-600', bgColor: 'bg-blue-100', percentage: 60 };
    return { level: 'Capacité élevée', color: 'text-green-600', bgColor: 'bg-green-100', percentage: 90 };
  };

  return (
    <div className="space-y-6">
      {/* Capacity Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Total Fournisseurs</h3>
            <Icon name="Users" className="w-5 h-5 text-blue-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{totalSuppliers}</p>
          <p className="text-xs text-gray-500 mt-1">Réseau global</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Total Machines</h3>
            <Icon name="Settings" className="w-5 h-5 text-green-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{totalMachines}</p>
          <p className="text-xs text-gray-500 mt-1">Capacité totale</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Capacité Moyenne</h3>
            <Icon name="TrendingUp" className="w-5 h-5 text-purple-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{averageCapacity}</p>
          <p className="text-xs text-gray-500 mt-1">Machines par marché</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Marchés Actifs</h3>
            <Icon name="Globe" className="w-5 h-5 text-orange-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{capacityData?.filter(c => c?.supplierCount > 0)?.length}</p>
          <p className="text-xs text-gray-500 mt-1">Avec fournisseurs</p>
        </div>
      </div>
      {/* Filter */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Filtrer par pays</label>
        <select
          value={selectedCountry}
          onChange={(e) => setSelectedCountry(e?.target?.value)}
          className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">Tous les pays</option>
          {countries?.map(country => (
            <option key={country?.id} value={country?.id}>
              {country?.countryName} ({country?.countryCode})
            </option>
          ))}
        </select>
      </div>
      {/* Capacity Planning Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Planification de la Capacité par Marché</h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Marché</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fournisseurs</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Machines</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Capacité Unitaire</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Niveau de Capacité</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Utilisation</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredData?.map(capacity => {
                const utilization = getCapacityUtilization(capacity?.machineCount);
                return (
                  <tr key={capacity?.countryId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Icon name="MapPin" className="w-4 h-4 text-gray-400" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{capacity?.countryName}</div>
                          <div className="text-sm text-gray-500">{capacity?.countryCode}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Icon name="Users" className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-semibold text-gray-900">{capacity?.supplierCount}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Icon name="Settings" className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-semibold text-gray-900">{capacity?.machineCount}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{capacity?.averageMachinesPerSupplier}</span>
                      <span className="text-xs text-gray-500 ml-1">machines/fournisseur</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${utilization?.bgColor} ${utilization?.color}`}>
                        {utilization?.level}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2 w-24">
                          <div 
                            className={`h-2 rounded-full ${utilization?.color?.replace('text-', 'bg-')}`}
                            style={{ width: `${utilization?.percentage}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium text-gray-700">{utilization?.percentage}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {filteredData?.length === 0 && (
            <div className="text-center py-12">
              <Icon name="Database" className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Aucune donnée de capacité disponible</p>
            </div>
          )}
        </div>
      </div>
      {/* Capacity Recommendations */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <Icon name="Lightbulb" className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Recommandations de Capacité</h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <Icon name="CheckCircle" className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Maintenir un minimum de 3 fournisseurs par marché stratégique pour assurer la continuité de service</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="CheckCircle" className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Équilibrer la charge entre fournisseurs avec un ratio optimal de 3-5 machines par fournisseur</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="CheckCircle" className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Identifier les marchés à forte demande nécessitant une expansion de capacité</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="CheckCircle" className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Prévoir des fournisseurs de secours pour les marchés critiques</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CapacityPlanningSection;